import React from 'react';
import { Briefcase } from 'lucide-react';

const colors = {
  primary: '#1A6FD6',
  navy: '#0D1F3C',
  body: '#6B7280',
  bodyMuted: '#9AA3B2',
  border: '#E5E9F0',
  surface: '#FFFFFF',
};

/** Convert "Mar 2024" -> "Mar-24", "May 2020" -> "May-20", "Present" -> "Present" */
const toShortDate = (s) => {
  if (!s) return '';
  const t = String(s).trim();
  if (/^present$/i.test(t) || /^till\s*date$/i.test(t)) return 'Present';
  const parts = t.split(/\s+/);
  if (parts.length === 1) {
    const y = parts[0];
    return y.length === 4 ? y.slice(-2) : y;
  }
  const month = parts[0].slice(0, 3);
  const year = parts[1];
  const shortYear = year && year.length === 4 ? year.slice(-2) : year;
  return `${month}-${shortYear}`;
};

const CompanyLogo = ({ exp }) => (
  <div
    className="w-10 h-10 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0"
    style={{ backgroundColor: '#F1F5FB', border: `1px solid ${colors.border}` }}
    data-testid="timeline-logo-wrap"
  >
    {exp.company_logo ? (
      <img
        src={exp.company_logo}
        alt={exp.company_name || 'company'}
        className="w-full h-full object-cover"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        data-testid="timeline-company-logo"
      />
    ) : (
      <Briefcase className="w-4 h-4" style={{ color: colors.primary }} />
    )}
  </div>
);

const TimelineRow = ({ exp }) => {
  const startShort = toShortDate(exp.start_date);
  const endShort = exp.is_current ? 'Present' : toShortDate(exp.end_date);
  return (
    <div className="flex gap-3 py-3" data-testid="timeline-item">
      <CompanyLogo exp={exp} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm" style={{ color: colors.navy }} data-testid="timeline-company">
          {exp.company_name || '—'}
        </p>
        <div className="flex items-baseline justify-between gap-3 mt-1">
          <span className="text-sm truncate" style={{ color: colors.body }} data-testid="timeline-title">
            {exp.job_title || '—'}
          </span>
          <span className="text-xs whitespace-nowrap" style={{ color: colors.bodyMuted }} data-testid="timeline-dates">
            {startShort}{startShort && endShort ? ' – ' : ''}{endShort}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Compact professional timeline — shows the last 3 roles only, one per row.
 * Header (profile picture + name) is intentionally omitted because the parent
 * already renders a profile-summary card directly above this component.
 */
const ProfessionalTimeline = ({
  experiences = [],
  /* eslint-disable no-unused-vars */
  totalExperience = null,
  profilePicture = '',
  fullName = '',
  /* eslint-enable no-unused-vars */
}) => {
  const top3 = (experiences || []).slice(0, 3);

  return (
    <div
      className="p-5 rounded-xl border"
      style={{ borderColor: colors.border, backgroundColor: colors.surface }}
      data-testid="professional-timeline"
    >
      {top3.length === 0 ? (
        <p className="text-sm text-center py-2" style={{ color: colors.body }}>
          No work history found on this profile.
        </p>
      ) : (
        <div className="divide-y" style={{ borderColor: colors.border }}>
          {top3.map((exp, idx) => (
            <TimelineRow key={`${exp.company_name}-${exp.start_date}-${idx}`} exp={exp} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfessionalTimeline;
